import Phaser from 'phaser';
import { Pipe } from '../entities/Pipe';
import type { Port } from '../entities/Port';
import { gridToWorld, isInsideGrid, worldToGrid } from '../grid';
import { fluidClassFor } from '../types';

/**
 * Manages the drag-to-build pipe interaction AND the tap-to-delete-pipe interaction.
 *
 * Drag model (bidirectional):
 *   • pointerdown on a port → start a Pipe at that connection cell.
 *   • pointermove → append/pop one segment per cell. Pipes may cross each other
 *     and ports they're not connected to; self-crossing is blocked in `Pipe.tryExtendTo`.
 *   • pointermove onto the second-to-last cell → backtrack (pop last segment).
 *   • pointerup on the OPPOSITE-kind port → finalize. Otherwise → cancel.
 *
 * Direction normalization:
 *   The data model is always spout → receptacle. If a drag started from a
 *   receptacle, `Pipe.reverseDirection` is called on release to swap from/to
 *   and reverse the segment array.
 *
 * Delete model (two-tap on a connected port — either end):
 *   • First tap on a cone-shaped port that already has a pipe attached at
 *     either end (spout source, receptacle destination, or bidirectional
 *     in either role) → mark it for deletion (X sprite drawn over it).
 *   • Second tap on the SAME marked port → destroy that pipe and clear the
 *     mark. `onPipeRemoved` fires so GameScene can refresh direct links.
 *   • Any pointerdown elsewhere → clear the mark and proceed with normal flow.
 *
 * Excluded ports:
 *   `shape: 'castellated'` ports (organ direct-link joints) are filtered out of
 *   drag start, drop, and delete-mark candidates.
 *
 * In-use guard:
 *   A port that's already a source OR destination of some pipe cannot be used
 *   for a new drag (one connection per port).
 */
export class PipeBuilder {
  private currentPipe: Pipe | null = null;

  /** True iff a pipe-build is in progress (pointer is dragging out a pipe). */
  isBuilding(): boolean {
    return this.currentPipe !== null;
  }
  // 'bidirectional' is treated like 'spout' for drag purposes — student
  // starts the drag from a port that's emitting (or willing to emit).
  private startedFromKind: 'spout' | 'receptacle' | 'bidirectional' = 'spout';
  private highlightedTarget: Port | null = null;

  // Delete-mark state — at most one receptacle marked at a time.
  private deleteMarkPort: Port | null = null;
  private deleteMarkSprite: Phaser.GameObjects.Image | null = null;

  constructor(
    private scene: Phaser.Scene,
    private getPorts: () => Port[],
    private getPipes: () => Pipe[],
    private onPipeFinalized: (pipe: Pipe) => void,
    private onPipeRemoved: (pipe: Pipe) => void,
  ) {
    scene.input.on('pointerdown', this.handlePointerDown, this);
    scene.input.on('pointermove', this.handlePointerMove, this);
    scene.input.on('pointerup', this.handlePointerUp, this);
    scene.input.on('pointerupoutside', this.handlePointerUp, this);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.cancelCurrent();
      this.clearDeleteMark();
    }
  }
  private enabled = true;

  cancelCurrent(): void {
    if (this.currentPipe) {
      this.currentPipe.destroyPipe();
      this.currentPipe = null;
    }
    this.clearHighlight();
  }

  /** Called by GameScene on CLEAR PIPES, level change, etc. */
  clearDeleteMark(): void {
    if (this.deleteMarkSprite) {
      this.deleteMarkSprite.destroy();
      this.deleteMarkSprite = null;
    }
    this.deleteMarkPort = null;
  }

  private clearHighlight(): void {
    if (this.highlightedTarget) {
      this.highlightedTarget.setHighlight(false);
      this.highlightedTarget = null;
    }
  }

  private setHighlight(port: Port | null): void {
    if (this.highlightedTarget === port) return;
    const wasNull = this.highlightedTarget === null;
    if (this.highlightedTarget) this.highlightedTarget.setHighlight(false);
    if (port) {
      port.setHighlight(true);
      if (wasNull) this.scene.sound.play('sfx-pipe-connect');
    }
    this.highlightedTarget = port;
  }

  /** Returns true if a port is a draggable endpoint (i.e., a cone-shaped port, not castellated). */
  private isPipePort(port: Port): boolean {
    return port.shape !== 'castellated';
  }

  /** Has any existing pipe already claimed this port (either as source or destination)? */
  private isPortInUse(port: Port): boolean {
    return this.getPipes().some((p) => p.fromPortId === port.id || p.toPortId === port.id);
  }

  /**
   * Find any pipe attached to this port — at EITHER end. Used by the
   * tap-to-delete flow so the student can remove a pipe by tapping the
   * source spout or the destination receptacle (or either end of a
   * bidirectional trachea pipe).
   */
  private pipeAtPort(port: Port): Pipe | null {
    return (
      this.getPipes().find((p) => p.fromPortId === port.id || p.toPortId === port.id) ?? null
    );
  }

  private handlePointerDown(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled) return;
    if (this.currentPipe) return;
    const cell = worldToGrid(pointer.worldX, pointer.worldY);
    if (!isInsideGrid(cell)) return;

    // Match a port if the press is on its connection cell OR on the cell
    // immediately adjacent in the OPPOSITE-of-facing direction (where the
    // cone visually extends — the cone is ~one cell long, protruding away
    // from the cell into the wall / organ body). Without this, a press on
    // the cone's wide end lands in the adjacent cell and the cell-exact
    // match below misses, letting the press fall through to e.g. an
    // organ-body drag handler.
    const portAtCell = this.getPorts().find((p) => {
      if (!this.isPipePort(p)) return false;
      if (p.connectionCell.col === cell.col && p.connectionCell.row === cell.row) {
        return true;
      }
      // Cell adjacent in the opposite-of-facing direction.
      let adjCol = p.connectionCell.col;
      let adjRow = p.connectionCell.row;
      switch (p.facing) {
        case 'E':
          adjCol -= 1;
          break;
        case 'W':
          adjCol += 1;
          break;
        case 'S':
          adjRow -= 1;
          break;
        case 'N':
          adjRow += 1;
          break;
      }
      return cell.col === adjCol && cell.row === adjRow;
    });

    // --- Delete flow ---
    // Tap on the SAME already-marked receptacle → confirm deletion.
    if (this.deleteMarkPort && portAtCell === this.deleteMarkPort) {
      this.confirmDeletion();
      return;
    }
    // Tap anywhere else → clear any existing mark (then fall through to normal logic).
    if (this.deleteMarkPort) this.clearDeleteMark();

    // Tap on any connected port — either end of the pipe — marks it for
    // delete and stops. Receptacle (incoming pipe), spout (outgoing pipe),
    // and bidirectional (in either role) all qualify. Don't start a drag.
    if (portAtCell && this.pipeAtPort(portAtCell)) {
      this.markForDelete(portAtCell);
      return;
    }

    // --- Drag flow (existing) ---
    if (!portAtCell) return;
    if (this.isPortInUse(portAtCell)) return;

    this.startedFromKind = portAtCell.kind;
    this.scene.sound.play('sfx-pipe-start');
    this.currentPipe = new Pipe(
      this.scene,
      portAtCell.id,
      portAtCell.connectionCell,
      portAtCell.facing,
      fluidClassFor(portAtCell.carries),
    );
  }

  private markForDelete(port: Port): void {
    this.deleteMarkPort = port;
    const world = gridToWorld(port.connectionCell);
    this.deleteMarkSprite = this.scene.add.image(world.x, world.y, 'burst-x');
    // Slightly smaller and above the port so it reads as a UI overlay, not a burst.
    this.deleteMarkSprite.setScale(0.85);
    this.deleteMarkSprite.setDepth(35);
  }

  private confirmDeletion(): void {
    const port = this.deleteMarkPort;
    if (!port) return;
    // `pipeAtPort` matches by either end, so this works for spouts (where
    // the marked port is `fromPortId`) and receptacles (`toPortId`).
    const pipe = this.pipeAtPort(port);
    this.clearDeleteMark();
    if (!pipe) return;
    pipe.destroyPipe();
    this.onPipeRemoved(pipe);
  }

  private handlePointerMove(pointer: Phaser.Input.Pointer): void {
    if (!this.enabled) return;
    if (!this.currentPipe) return;
    const cell = worldToGrid(pointer.worldX, pointer.worldY);
    if (!isInsideGrid(cell)) return;

    // Backtrack: if the pointer moves onto the second-to-last cell, remove the last segment.
    const segs = this.currentPipe.segments;
    if (segs.length >= 2) {
      const secondLast = segs[segs.length - 2];
      if (secondLast.col === cell.col && secondLast.row === cell.row) {
        this.currentPipe.popLastSegment();
        this.updateTargetHighlight();
        return;
      }
    }

    // Pipes are allowed to cross each other (Step Four+ uses crossings for
    // realistic routing). Only a pipe crossing ITSELF is disallowed (Pipe.tryExtendTo).
    const lenBefore = this.currentPipe.segments.length;
    this.currentPipe.tryExtendTo(cell);
    if (this.currentPipe.segments.length > lenBefore) {
      this.scene.sound.play('sfx-pipe-new-link');
    }
    this.updateTargetHighlight();
  }

  /**
   * After any extend / pop, check whether the pipe's last cell is on a candidate
   * target port's connection cell. If yes, highlight as "ready to lock"; else clear.
   * The pipe is NOT finalized here — only on pointer release.
   */
  private updateTargetHighlight(): void {
    if (!this.currentPipe) {
      this.clearHighlight();
      return;
    }
    const target = this.findTargetAt(this.currentPipe.lastCell());
    this.setHighlight(target);
  }

  /**
   * Find a valid drop-target port at the given cell. Foul connections are
   * allowed: spout→spout and receptacle→receptacle drop targets pass through
   * here just like normal ones. The student finds out it's wrong only when
   * they press START FLUIDS (spout→spout bursts immediately; receptacle→
   * receptacle does nothing). The only exclusions are:
   *   • the starting port itself (can't drop where you started),
   *   • castellated ports (organ direct-link joints — pipes can't reach them),
   *   • ports already in use by another pipe.
   */
  private findTargetAt(cell: { col: number; row: number }): Port | null {
    if (!this.currentPipe) return null;
    const startingPortId = this.currentPipe.fromPortId;
    const candidate = this.getPorts().find(
      (p) =>
        this.isPipePort(p) &&
        p.id !== startingPortId &&
        p.connectionCell.col === cell.col &&
        p.connectionCell.row === cell.row,
    );
    if (!candidate) return null;
    if (this.isPortInUse(candidate)) return null;
    return candidate;
  }

  private handlePointerUp(_pointer: Phaser.Input.Pointer): void {
    if (!this.enabled) return;
    if (!this.currentPipe) {
      this.clearHighlight();
      return;
    }
    const target = this.findTargetAt(this.currentPipe.lastCell());
    if (!target) {
      this.cancelCurrent();
      return;
    }
    this.scene.sound.play('sfx-pipe-complete');

    if (this.startedFromKind === 'spout' || this.startedFromKind === 'bidirectional') {
      // For spout→spout pipes, drag direction matters: the pipe's `fromPortId`
      // determines which spout's emission drives the flow. If the student
      // dragged from a rule-governed spout (e.g. brain-out, gated by
      // `dependsOn` / `transform`) to an unconditional spout (wall spout,
      // always emitting), we want the unconditional spout as the source so
      // the pipe actually flows. Detect that case and flip; otherwise leave
      // start as source.
      // (Bidirectional ports are treated like spouts here — the trachea's
      // actual flow direction is managed by TracheaFlowFx, not the data
      // model orientation.)
      if (target.kind === 'spout' || target.kind === 'bidirectional') {
        const ports = this.getPorts();
        const startPort = ports.find((p) => p.id === this.currentPipe!.fromPortId);
        const startIsActive =
          !!startPort && startPort.dependsOn.length === 0 && !startPort.transform;
        const targetIsActive = target.dependsOn.length === 0 && !target.transform;
        if (!startIsActive && targetIsActive) {
          this.currentPipe.reverseDirection(
            target.id,
            target.facing,
            fluidClassFor(target.carries),
          );
        } else {
          this.currentPipe.connectTo(target.id);
        }
      } else {
        // Standard spout → receptacle: pipe.fromPortId is already the spout.
        this.currentPipe.connectTo(target.id);
      }
    } else {
      // Drag started from a receptacle. Flip the pipe so it goes spout → receptacle.
      this.currentPipe.reverseDirection(target.id, target.facing, fluidClassFor(target.carries));
    }

    // Trachea detection: a pipe between two bidirectional ports is a special
    // trachea pipe — segments stay gray, sprite flow is alternating
    // single-shot. (Endpoint kinds may be 'spout' or 'bidirectional' on each
    // end of the lookup; the test is simply "both ports are bidirectional.")
    const fromPort = this.getPorts().find((p) => p.id === this.currentPipe!.fromPortId);
    const toPort = this.getPorts().find((p) => p.id === this.currentPipe!.toPortId);
    if (fromPort?.kind === 'bidirectional' && toPort?.kind === 'bidirectional') {
      this.currentPipe.specialKind = 'trachea';
    }

    this.onPipeFinalized(this.currentPipe);
    this.currentPipe = null;
    this.clearHighlight();
  }
}
